import { apiFetch } from '@/api/client'
import { createWorkspaceResourceApi } from '@/api/endpoints/workspaceResource'

const audits = createWorkspaceResourceApi('audits')
const templates = createWorkspaceResourceApi('audit-templates')
const multipart = { 'Content-Type': 'multipart/form-data' }

const post = (url, body, headers) => apiFetch(url, { method: 'POST', body, headers })
const auditUrl = (workspaceId, auditId) => `${audits.path(workspaceId)}/${auditId}`

/** Workspace admin side: `/workspaces/{workspaceId}/audits`. */
export const auditsApi = {
  list: audits.list,
  get: audits.get,
  create: audits.create,
  update: audits.update,
  remove: audits.remove,

  issueReadiness: (workspaceId, auditId, { signal } = {}) =>
    apiFetch(`${auditUrl(workspaceId, auditId)}/issue-readiness`, { signal }),
  issue: (workspaceId, auditId, body, headers) => post(`${auditUrl(workspaceId, auditId)}/issue`, body, headers),
  sendReminder: (workspaceId, auditId, headers) =>
    post(`${auditUrl(workspaceId, auditId)}/send-reminder`, {}, headers),
  roomLink: (workspaceId, auditId, body) => post(`${auditUrl(workspaceId, auditId)}/room-link`, body),

  addCategoryItem: (workspaceId, auditId, categoryId, body) =>
    post(`${auditUrl(workspaceId, auditId)}/categories/${categoryId}/items`, body),
  evidenceUploadUrl: (workspaceId, auditId, body) =>
    post(`${auditUrl(workspaceId, auditId)}/evidence/upload-url`, body),
  submitEvidence: (workspaceId, auditId, formData) =>
    post(`${auditUrl(workspaceId, auditId)}/evidence`, formData, multipart),
  reviewItem: (workspaceId, auditId, itemId, body) =>
    post(`${auditUrl(workspaceId, auditId)}/items/${itemId}/review`, body),
  notifyClientItemSentBack: (workspaceId, auditId, itemId) =>
    post(`${auditUrl(workspaceId, auditId)}/items/${itemId}/notify-client`),

  readiness: (workspaceId, auditId, { signal } = {}) =>
    apiFetch(`${auditUrl(workspaceId, auditId)}/readiness`, { signal }),
  saveCategoryConclusion: (workspaceId, auditId, categoryId, body) =>
    post(`${auditUrl(workspaceId, auditId)}/categories/${categoryId}/conclusion`, body),

  startReport: (workspaceId, auditId) => post(`${auditUrl(workspaceId, auditId)}/report`),
  generateReportSection: (workspaceId, auditId, sectionId) =>
    post(`${auditUrl(workspaceId, auditId)}/report/sections/${sectionId}/generate`),
  approveReportSection: (workspaceId, auditId, sectionId, body) =>
    post(`${auditUrl(workspaceId, auditId)}/report/sections/${sectionId}/approve`, body),
  reportComments: (workspaceId, auditId, { signal } = {}) =>
    apiFetch(`${auditUrl(workspaceId, auditId)}/report/comments`, { signal }),
  reviseReport: (workspaceId, auditId) => post(`${auditUrl(workspaceId, auditId)}/report/revise`),

  attest: (workspaceId, auditId, body) => post(`${auditUrl(workspaceId, auditId)}/attest`, body),
  transition: (workspaceId, auditId, status) =>
    post(`${auditUrl(workspaceId, auditId)}/transition`, { status }),

  categoryGaps: (workspaceId, auditId, categoryId, { signal } = {}) =>
    apiFetch(`${auditUrl(workspaceId, auditId)}/categories/${categoryId}/gaps`, { signal }),
  addGap: (workspaceId, auditId, categoryId, body) =>
    post(`${auditUrl(workspaceId, auditId)}/categories/${categoryId}/gaps`, body),
  deleteGap: (workspaceId, auditId, categoryId, gapId) =>
    apiFetch(`${auditUrl(workspaceId, auditId)}/categories/${categoryId}/gaps/${gapId}`, { method: 'DELETE' }),
}

/** Client portal (auditee) side: `/auditee/audits`. */
export const auditeeAuditsApi = {
  summary: ({ signal } = {}) => apiFetch('auditee/audits/summary', { signal }),
  list: (params, { signal } = {}) => apiFetch('auditee/audits', { params, signal }),
  get: (auditId, { signal } = {}) => apiFetch(`auditee/audits/${auditId}`, { signal }),
  submitEvidence: (auditId, formData) =>
    post(`auditee/audits/${auditId}/evidence`, formData, multipart),
  report: (auditId, { signal } = {}) => apiFetch(`auditee/audits/${auditId}/report`, { signal }),
  postReportComment: (auditId, body) => post(`auditee/audits/${auditId}/report/comments`, body),
  categoryGaps: (auditId, categoryId, { signal } = {}) =>
    apiFetch(`auditee/audits/${auditId}/categories/${categoryId}/gaps`, { signal }),
  addGap: (auditId, categoryId, body) =>
    post(`auditee/audits/${auditId}/categories/${categoryId}/gaps`, body),
  deleteGap: (auditId, categoryId, gapId) =>
    apiFetch(`auditee/audits/${auditId}/categories/${categoryId}/gaps/${gapId}`, { method: 'DELETE' }),
}

export const auditTemplatesApi = {
  list: templates.list,
  get: templates.get,
  default: (workspaceId) => apiFetch(`${templates.path(workspaceId)}/default`),
  create: templates.create,
  update: templates.update,
  remove: templates.remove,
}
